/**
 * Shop Header Component
 * Displays shop name, owner, logged-in user icon, and menu
 * Uses theme colors from shop configuration
 * Supports two modes:
 * - Home mode: Shop info + User menu (default)
 * - Navigation mode: Home button + Page title + Menu button
 */

import React, { useState, useMemo, useEffect } from 'react';
import { View, TouchableOpacity, StyleSheet, Modal, ScrollView, Image, useWindowDimensions } from 'react-native';
import { useAuthTokens, useCurrentShop, useThemeColors, useSubscriptionAccess, storageService } from '@app/core';
import { Button, Text } from '@app/design-system';
import { colors } from '@app/design-system';
import { UserDetailsModal } from './UserDetailsModal';

interface ShopHeaderProps {
  shopImage?: string; // Shop image URL for header display
  shopName?: string; // Shop name override
  onManageShop?: () => void;
  onManageUser?: () => void;
  onManageCatalog?: () => void;
  onManageOrders?: () => void;
  onWebsiteBuilder?: () => void; // Navigate to Website Builder
  onLogout?: () => void;
  onHome?: () => void; // Navigate to home
  mode?: 'home' | 'navigation'; // Header mode
  title?: string; // Page title for navigation mode
}

// Default shop image path
const DEFAULT_SHOP_IMAGE = '/assets/icons/DefaultShop.png';

export const ShopHeader: React.FC<ShopHeaderProps> = ({
  shopImage,
  shopName,
  onManageShop,
  onManageUser,
  onManageCatalog,
  onManageOrders,
  onWebsiteBuilder,
  onLogout,
  onHome,
  mode = 'home',
  title,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [loadedImageUrl, setLoadedImageUrl] = useState<string | null>(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const { width } = useWindowDimensions(); // For responsive sizing
  const shop = useCurrentShop();
  const tokens = useAuthTokens();
  const themeColors = useThemeColors();
  const { hasAccess, subscriptionStatus } = useSubscriptionAccess();

  // Load shop image if it's a blob path
  useEffect(() => {
    if (shopImage) {
      loadImageUrl(shopImage);
    } else {
      setLoadedImageUrl(null);
    }
  }, [shopImage]);

  /**
   * Load image URL from blob path or direct URL
   */
  const loadImageUrl = async (imagePath: string) => {
    try {
      setLoadingImage(true);
      setImageError(false);

      // Check if it's already a full URL
      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        setLoadedImageUrl(imagePath);
        return;
      }

      // If it's a relative path like /assets/...
      if (imagePath.startsWith('/')) {
        setLoadedImageUrl(imagePath);
        return;
      }

      // Otherwise, treat as blob path and get read URL
      const readUrl = await storageService.getReadUrl(imagePath);
      setLoadedImageUrl(readUrl);
    } catch (error) {
      console.error('❌ [ShopHeader] Failed to load image:', error);
      setImageError(true);
      setLoadedImageUrl(null);
    } finally {
      setLoadingImage(false);
    }
  };

  // Legacy subscription info for backward compatibility
  const subscriptionInfo = useMemo(() => ({
    hasActivePlan: subscriptionStatus.hasActivePlan,
    planName: subscriptionStatus.planName || 'No Active Plan',
    daysLeft: subscriptionStatus.daysRemaining,
    expiryDate: subscriptionStatus.expiryDate,
    isExpired: subscriptionStatus.isExpired
  }), [subscriptionStatus]);

  const formatExpiryDate = (date: Date | null) => {
    if (!date) return 'N/A';
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    // Add ordinal suffix
    const suffix = day === 1 || day === 21 || day === 31 ? 'st' :
                   day === 2 || day === 22 ? 'nd' :
                   day === 3 || day === 23 ? 'rd' : 'th';

    return `${day}${suffix} ${month} ${year}`;
  };

  const handleUserDetails = () => {
    setShowMenu(false);
    setShowUserDetails(true);
  };

  const handleSendFeedback = () => {
    setShowMenu(false);
    const phoneNumber = '918788450037'; // +91 8788450037
    const message = 'I would like to share feedback for this pilot app as :';
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

    // Open WhatsApp in new window/tab
    if (typeof window !== 'undefined') {
      window.open(whatsappUrl, '_blank');
    }
  };

  const handleLogout = async (): Promise<void> => {
    try {
      setShowMenu(false);
      if (onLogout) {
        onLogout();
      }
    } catch (error) {
      console.error('Logout failed', error);
    }
  };

  const handleMenuAction = (action?: () => void) => {
    if (!subscriptionInfo.hasActivePlan) {
      // Don't allow actions if no active plan
      setShowMenu(false);
      return;
    }
    if (action) {
      action();
    }
    setShowMenu(false);
  };

  // Render Home Mode Header (Shop info + Smart positioned shop image + User menu)
  const renderHomeHeader = () => {
    // Dynamic responsive image sizing with smart positioning
    // Small screens: Right side (55px)
    // Medium/Large screens: Centered (80-120px)
    const getImageSize = () => {
      if (width < 640) return 55;   // Small - right side, compact
      if (width < 768) return 80;   // Medium start - centered
      if (width < 1024) return 95;  // Tablet - centered, larger
      if (width < 1280) return 110; // Laptop - centered, larger
      return 120;                    // Desktop - centered, largest
    };

    // Determine if image should be centered (medium/large) or right-aligned (small)
    const isCentered = width >= 640;
    const imageSize = getImageSize();

    // Use shop image or default image
    const displayImage = (loadedImageUrl && !imageError) ? loadedImageUrl : DEFAULT_SHOP_IMAGE;

    return (
      <View
        style={[
          styles.header,
          { backgroundColor: themeColors.primary },
        ]}
      >
        {/* Left - Shop Info */}
        <View style={styles.shopInfoLeft}>
          <View style={styles.shopText}>
            <Text variant="h3" bold style={styles.shopName}>
              {shopName || shop?.name || 'My Shop'}
            </Text>
            <Text variant="caption" color="secondary" style={styles.ownerText}>
              Managed by {shop?.owner || 'N/A'}
            </Text>
            {/* Subscription Info */}
            <View style={styles.subscriptionBadge}>
              <Text style={[
                styles.subscriptionText,
                !subscriptionInfo.hasActivePlan && styles.noActivePlanText
              ]}>
                {subscriptionInfo.hasActivePlan
                  ? `${subscriptionInfo.planName} • Expires ${formatExpiryDate(subscriptionInfo.expiryDate)}`
                  : 'No Active Plan - Please Contact Admin'
                }
              </Text>
            </View>
          </View>
        </View>

        {/* Shop Image - Smart positioned (right on small, centered on medium/large) with tap to enlarge */}
        <TouchableOpacity
          style={[
            isCentered ? styles.headerImageContainerCenter : styles.headerImageContainerRight,
          ]}
          onPress={() => setShowImageModal(true)}
          activeOpacity={0.8}
        >
          <Image
            source={{ uri: displayImage }}
            style={[
              styles.headerShopImage,
              {
                width: imageSize,
                height: imageSize,
                borderRadius: imageSize / 2,
              }
            ]}
            resizeMode="cover"
            onError={() => setImageError(true)}
          />
          {/* Tap hint overlay - visible on hover/press */}
          <View style={styles.imageHintOverlay}>
            <Text style={styles.imageHintText}>👆</Text>
          </View>
        </TouchableOpacity>

        {/* Right - Menu Button (3-dot icon) */}
        <View style={styles.headerRight}>
          <TouchableOpacity
            onPress={() => setShowMenu(true)}
            style={styles.userButton}
            testID="open-menu"
          >
            <Text style={styles.userIcon}>⋮</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  // Render Navigation Mode Header (Home button + Title + Menu button)
  const renderNavigationHeader = () => (
    <View
      style={[
        styles.headerWrapper,
        { backgroundColor: themeColors.primary },
      ]}
    >
      <View style={styles.header}>
        {/* Home Button */}
        <TouchableOpacity
          onPress={onHome}
          style={styles.homeButton}
          testID="home-button"
        >
          <Text style={styles.homeIcon}>🏠</Text>
        </TouchableOpacity>

        {/* Page Title */}
        <View style={styles.titleContainer}>
          <Text variant="h3" bold style={styles.pageTitle}>
            {title || shop?.name || 'My Shop'}
          </Text>
        </View>

        {/* Menu Button */}
        <TouchableOpacity
          onPress={() => setShowMenu(true)}
          style={styles.menuButton}
          testID="open-menu"
        >
          <Text style={styles.menuIcon}>⋮</Text>
        </TouchableOpacity>
      </View>

      {/* Subscription Info Row */}
      <View style={styles.subscriptionRow}>
        <Text style={[
          styles.subscriptionRowText,
          !subscriptionInfo.hasActivePlan && styles.noActivePlanText
        ]}>
          {subscriptionInfo.hasActivePlan
            ? `${subscriptionInfo.planName} • Expires ${formatExpiryDate(subscriptionInfo.expiryDate)}`
            : 'No Active Plan - Please Contact Admin'
          }
        </Text>
      </View>
    </View>
  );

  return (
    <>
      {/* Render appropriate header based on mode */}
      {mode === 'home' ? renderHomeHeader() : renderNavigationHeader()}


      {/* Menu Modal */}
      <Modal
        visible={showMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowMenu(false)}
      >
        <TouchableOpacity
          style={styles.overlay}
          activeOpacity={1}
          onPress={() => setShowMenu(false)}
        >
          <View style={styles.menuContainer}>
            <ScrollView style={styles.menuContent}>
              {/* Subscription Status Banner (if no active plan) */}
              {!subscriptionInfo.hasActivePlan && (
                <>
                  <View style={styles.warningBanner}>
                    <Text style={styles.warningText}>
                      ⚠️ No Active Plan - Menu options disabled
                    </Text>
                    <Text style={styles.warningSubtext}>
                      Please contact your administrator to activate a subscription plan
                    </Text>
                  </View>
                  <View style={styles.divider} />
                </>
              )}

              {/* Management menu items - Disabled if no active plan */}
              <View style={[styles.menuItem, !subscriptionInfo.hasActivePlan && styles.disabledMenuItem]}>
                <Button
                  variant="ghost"
                  onPress={() => handleMenuAction(onManageOrders)}
                  disabled={!subscriptionInfo.hasActivePlan}
                  testID="menu-manage-orders"
                >
                  <Text style={!subscriptionInfo.hasActivePlan && styles.disabledText}>
                    📦 Manage Orders
                  </Text>
                </Button>
              </View>

              <View style={[styles.menuItem, !subscriptionInfo.hasActivePlan && styles.disabledMenuItem]}>
                <Button
                  variant="ghost"
                  onPress={() => handleMenuAction(onManageCatalog)}
                  disabled={!subscriptionInfo.hasActivePlan}
                  testID="menu-manage-catalog"
                >
                  <Text style={!subscriptionInfo.hasActivePlan && styles.disabledText}>
                    📋 Manage Catalog
                  </Text>
                </Button>
              </View>

              <View style={[styles.menuItem, !subscriptionInfo.hasActivePlan && styles.disabledMenuItem]}>
                <Button
                  variant="ghost"
                  onPress={() => handleMenuAction(onManageShop)}
                  disabled={!subscriptionInfo.hasActivePlan}
                  testID="menu-manage-shop"
                >
                  <Text style={!subscriptionInfo.hasActivePlan && styles.disabledText}>
                    📦 Manage Shop
                  </Text>
                </Button>
              </View>

              <View style={[styles.menuItem, !subscriptionInfo.hasActivePlan && styles.disabledMenuItem]}>
                <Button
                  variant="ghost"
                  onPress={() => handleMenuAction(onManageUser)}
                  disabled={!subscriptionInfo.hasActivePlan}
                  testID="menu-manage-user"
                >
                  <Text style={!subscriptionInfo.hasActivePlan && styles.disabledText}>
                    👥 Manage Users
                  </Text>
                </Button>
              </View>

              <View style={styles.divider} />

              {/* My Details Menu Item (Always available) */}
              <View style={styles.menuItem}>
                <Button
                  variant="ghost"
                  onPress={handleUserDetails}
                  testID="menu-user-details"
                >
                  👤 My Details
                </Button>
              </View>

              <View style={styles.divider} />

              {/* Website Builder - Disabled if no active plan */}
              <View style={[styles.menuItem, !subscriptionInfo.hasActivePlan && styles.disabledMenuItem]}>
                <Button
                  variant="ghost"
                  onPress={() => handleMenuAction(onWebsiteBuilder)}
                  disabled={!subscriptionInfo.hasActivePlan}
                  testID="menu-website-builder"
                >
                  <Text style={!subscriptionInfo.hasActivePlan && styles.disabledText}>
                    🌐 Website Builder
                  </Text>
                </Button>
              </View>

              <View style={styles.divider} />

              {/* Send Feedback & Ask Support - Always available */}
              <View style={styles.menuItem}>
                <Button
                  variant="ghost"
                  onPress={handleSendFeedback}
                  testID="menu-send-feedback"
                >
                  💬 Send Feedback & Ask Support
                </Button>
              </View>

              <View style={styles.divider} />

              {/* Logout - Always available */}
              <View style={styles.menuItem}>
                <Button
                  variant="ghost"
                  onPress={handleLogout}
                  testID="menu-logout"
                >
                  🚪 Logout
                </Button>
              </View>
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* User Details Modal */}
      <UserDetailsModal
        visible={showUserDetails}
        onClose={() => setShowUserDetails(false)}
        userDetails={
          tokens
            ? {
                userId: tokens.userId,
                shopId: tokens.shopId,
                authLevel: tokens.authLevel,
                oauthToken: tokens.oauthToken,
                publicAccessToken: tokens.publicAccessToken,
                expiresAt: tokens.expiresAt,
              }
            : null
        }
      />

      {/* Shop Image Enlargement Modal - Tap to close, with edit option */}
      <Modal
        visible={showImageModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowImageModal(false)}
      >
        <TouchableOpacity
          style={styles.imageModalOverlay}
          activeOpacity={1}
          onPress={() => setShowImageModal(false)}
        >
          {/* Large shop image - fits screen perfectly */}
          <Image
            source={{ uri: (loadedImageUrl && !imageError) ? loadedImageUrl : DEFAULT_SHOP_IMAGE }}
            style={styles.imageModalImage}
            resizeMode="contain"
          />

          {/* Edit Image Button - positioned at bottom */}
          <TouchableOpacity
            style={styles.editImageButton}
            onPress={(e) => {
              e.stopPropagation(); // Prevent modal close
              setShowImageModal(false);
              if (onManageShop) {
                onManageShop(); // Navigate to Manage Shop screen
              }
            }}
            activeOpacity={0.8}
          >
            <Text style={styles.editImageButtonText}>✏️ Edit Shop Image</Text>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  headerWrapper: {
    // Wrapper for navigation mode with subscription row
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  // Home mode styles - 3 section layout (Left, Center, Right)
  shopInfoLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0, // Allow text truncation
  },
  shopInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0, // Allow text truncation
  },
  shopText: {
    flex: 1,
    minWidth: 0, // Allow text truncation
  },
  shopName: {
    color: colors.white,
    fontSize: 16,
    marginBottom: 2,
  },
  ownerText: {
    color: colors.white,
    opacity: 0.8,
    fontSize: 12,
    marginBottom: 4,
  },
  headerImageContainerCenter: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    // @ts-ignore - transform is valid for web
    transform: 'translate(-50%, -50%)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  headerImageContainerRight: {
    marginLeft: 8,
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerImageContainer: {
    marginLeft: 12,
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerShopImage: {
    borderWidth: 3,
    borderColor: colors.white,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.4)',
    elevation: 8,
    backgroundColor: colors.white, // Fallback background
  },
  imageHintOverlay: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 12,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  imageHintText: {
    fontSize: 14,
    color: colors.white,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minWidth: 50, // Ensure consistent width for balance
  },
  subscriptionBadge: {
    marginTop: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  subscriptionText: {
    color: colors.white,
    fontSize: 10,
    fontWeight: '600',
  },
  subscriptionRow: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.1)',
  },
  subscriptionRowText: {
    color: colors.white,
    fontSize: 11,
    textAlign: 'center',
    fontWeight: '600',
  },
  noActivePlanText: {
    color: '#FF6B6B',
    fontWeight: 'bold',
  },
  userButton: {
    padding: 8,
    marginLeft: 12,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  userIcon: {
    fontSize: 24,
    color: colors.white,
  },
  // Navigation mode styles
  homeButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  homeIcon: {
    fontSize: 20,
    color: colors.white,
  },
  titleContainer: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 12,
  },
  pageTitle: {
    color: colors.white,
    fontSize: 18,
    textAlign: 'center',
  },
  menuButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  menuIcon: {
    fontSize: 24,
    color: colors.white,
    fontWeight: 'bold',
  },
  // Modal styles
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  menuContainer: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '80%',
    paddingTop: 8,
  },
  menuContent: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  menuItem: {
    paddingVertical: 4,
  },
  disabledMenuItem: {
    opacity: 0.4,
  },
  disabledText: {
    color: colors.gray4,
  },
  warningBanner: {
    backgroundColor: '#FFF3CD',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  warningText: {
    color: '#856404',
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  warningSubtext: {
    color: '#856404',
    fontSize: 12,
  },
  divider: {
    height: 1,
    backgroundColor: colors.gray3,
    marginVertical: 12,
  },
  // Image Modal Styles - Simple tap-to-close with full-screen image
  imageModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageModalImage: {
    width: '100%',
    height: '100%',
    maxWidth: '95%',
    maxHeight: '95%',
  },
  editImageButton: {
    position: 'absolute',
    bottom: 40,
    backgroundColor: colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 25,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
    elevation: 8,
  },
  editImageButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
});



